import React from 'react';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { useTranslation } from '@/i18n';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message?: string;
  /** Redacted, human-readable summary lines of the action being confirmed. */
  preview?: string[];
  confirmLabel?: string;
  cancelLabel?: string;
  /** Danger styling for destructive actions (delete). */
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * STORY-155 — human-in-the-loop confirmation for a state-changing write. Shows a
 * summary of the pending action and requires an explicit click before it runs.
 * Reused for alert-rule save (create/edit) and delete.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  preview,
  confirmLabel,
  cancelLabel,
  danger,
  loading,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onCancel}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <ShieldAlert className={danger ? 'h-5 w-5 text-red-500' : 'h-5 w-5 text-amber-500'} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        </div>
        <div className="p-5 space-y-3">
          {message && <p className="text-sm text-gray-600 dark:text-gray-300">{message}</p>}
          {preview && preview.length > 0 && (
            <ul className="rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60">
              {preview.map((line, i) => (
                <li key={i} className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 break-words">
                  {line}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            {cancelLabel || t.common.cancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 flex items-center gap-2 ${
              danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel || t.common.confirm}
          </button>
        </div>
      </div>
    </div>
  );
};
